import { HttpResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { APP } from 'src/app/config/app';
import { IExportableList, IListExportPage, IListExportRowCounts } from 'src/app/models';
import { ExportableListType, ListExportScope } from 'src/app/models/enums';
import { UtilsService } from '../utils/utils.service';
import { ListExportHttpService } from './list-export.http.service';

@Injectable({
  providedIn: 'root',
})
export class ListExportService {
  private readonly exportableListState = signal<IExportableList | null>(null);
  readonly exportableList = this.exportableListState.asReadonly();

  constructor(
    private listExportHttpService: ListExportHttpService,
    private utilsService: UtilsService,
  ) {}

  register(
    type: ExportableListType,
    exportRows: (visiblePage?: IListExportPage) => Promise<string>,
    visiblePage: IListExportPage,
    rowCounts: IListExportRowCounts | null = null,
  ) {
    this.exportableListState.set({
      type,
      rowCounts,
      startExport: (scope) => exportRows(scope === ListExportScope.VISIBLE ? visiblePage : undefined),
    });
  }

  setRowCounts(type: ExportableListType, rowCounts: IListExportRowCounts) {
    this.exportableListState.update((exportableList) =>
      exportableList?.type === type ? { ...exportableList, rowCounts } : exportableList,
    );
  }

  // The next page registers its list before the previous one is destroyed, so only the owner may clear it
  clear(type: ExportableListType) {
    if (this.exportableListState()?.type === type) this.exportableListState.set(null);
  }

  async download(exportPath: string) {
    const response = (await lastValueFrom(
      this.listExportHttpService.downloadExport(exportPath),
    )) as HttpResponse<Blob>;
    const fileName = exportPath.split('/').pop() ?? APP.LIST_EXPORT_DEFAULT_FILE_NAME;
    this.utilsService.downloadBlob(response.body!, fileName, APP.LIST_EXPORT_MIME_TYPE);
  }
}
